// VehicleForm — one row of vehicle inputs.
//
// Used by:
//   - BookingCreatePage (1–3 instances inside a vehicle accordion)
//   - BookingEditPage (single instance — server PUT is per-vehicle)
//
// Per the server schema (server/src/modules/scrap/bookings/bookings.types.ts
// `vehicleInputSchema`), each vehicle carries:
//   - car_make_model (single string — NOT split on the server)
//   - car_registration (validated; UK reg pattern is enforced client-side
//     as a soft warning, not a hard block — DVLA accepts a few off-spec
//     formats and the server only normalises whitespace)
//   - product_id (nullable scrap-type ID)
//   - price (number, 2dp, ≥0)
//   - notes (per-vehicle, optional)
//
// The brief listed split make/model/colour and per-vehicle payment_mode;
// those don't exist on the server schema. Documented as a Wave B3
// brief→server deviation in the wave summary.
//
// Duplicate-registration check fires onBlur of the registration input
// (debounced via the parent's batched effect — see useBookingForm).

import { useId } from 'react';
import { Trash2 } from 'lucide-react';
import { Input } from '../../../components/form/Input';
import { Textarea } from '../../../components/form/Textarea';
import { Select } from '../../../components/form/Select';
import { Button } from '../../../components/ui/Button/Button';
import type { ReferenceDataItem } from '../../../types/api';
import styles from './VehicleForm.module.css';

// Loose UK reg pattern: 1–4 letters or digits, optional space, more
// alphanumerics. Plates like "MX59 ABC", "AB12 XYZ", "ABC 123" all pass.
// Used for the soft-warning helper text only — submission accepts any
// non-empty 1–20-char string per the server.
export const UK_REG_PATTERN =
  /^([A-Z]{1,2}\d{1,4}\s?[A-Z]{1,3}|[A-Z]{1,3}\s?\d{1,4})$/i;

export interface VehicleFormValue {
  car_make_model: string;
  car_registration: string;
  product_id: number | null;
  price: string; // bound as text so users can type "120.5" mid-edit
  notes: string;
}

export interface VehicleFormProps {
  index: number;
  total: number;
  value: VehicleFormValue;
  products: ReferenceDataItem[];
  /** Whether reference-data is still loading. */
  productsLoading?: boolean;
  /** Map of duplicate-warning text by vehicle index. The parent owns the
   *  duplicate-check fetch (see useBookingForm). */
  duplicateWarning?: string | null;
  onChange: (next: VehicleFormValue) => void;
  onRemove?: () => void;
  onRegBlur?: (registration: string) => void;
}

export function VehicleForm({
  index,
  total,
  value,
  products,
  productsLoading = false,
  duplicateWarning,
  onChange,
  onRemove,
  onRegBlur,
}: VehicleFormProps): JSX.Element {
  const idBase = useId();

  function patch(p: Partial<VehicleFormValue>) {
    onChange({ ...value, ...p });
  }

  const productOptions = products.map((p) => ({
    value: String(p.id),
    label: p.name,
  }));

  return (
    <fieldset className={styles.wrap} aria-label={`Vehicle ${index + 1}`}>
      <legend className={styles.legend}>
        <span className={styles.legendText}>
          Vehicle {index + 1} of {total}
        </span>
        {onRemove ? (
          <Button
            variant="ghost"
            size="sm"
            icon={Trash2}
            onClick={onRemove}
            aria-label={`Remove vehicle ${index + 1}`}
          >
            Remove
          </Button>
        ) : null}
      </legend>

      <div className={styles.grid}>
        <Input
          label="Registration"
          required
          value={value.car_registration}
          onChange={(e) =>
            patch({ car_registration: e.target.value.toUpperCase() })
          }
          onBlur={() =>
            onRegBlur?.(value.car_registration.trim().toUpperCase())
          }
          placeholder="AB12 XYZ"
          autoComplete="off"
          spellCheck={false}
          id={`${idBase}-reg`}
          helper={
            value.car_registration.trim() &&
            !UK_REG_PATTERN.test(value.car_registration.trim())
              ? 'This does not look like a UK plate — double-check before saving.'
              : undefined
          }
        />
        <Input
          label="Make and model"
          required
          value={value.car_make_model}
          onChange={(e) => patch({ car_make_model: e.target.value })}
          placeholder="Ford Focus 1.6 TDCi"
          id={`${idBase}-mm`}
        />
        <Select
          label="Scrap type"
          options={productOptions}
          placeholder={
            productsLoading
              ? 'Loading…'
              : productOptions.length === 0
                ? 'No options — leave blank'
                : '—'
          }
          value={value.product_id !== null ? String(value.product_id) : ''}
          onChange={(e) =>
            patch({
              product_id: e.target.value === '' ? null : Number(e.target.value),
            })
          }
          id={`${idBase}-product`}
        />
        <Input
          label="Price"
          required
          variant="number"
          inputMode="decimal"
          step="0.01"
          min="0"
          value={value.price}
          onChange={(e) => patch({ price: e.target.value })}
          placeholder="0.00"
          helper="Pounds Sterling — two decimal places."
          id={`${idBase}-price`}
        />
      </div>

      {/* Per-vehicle notes only make sense when there's more than one
          vehicle on the booking — with a single vehicle the shared
          "Notes" card on the parent page covers the same DB column. The
          parent (useBookingForm.removeVehicle) clears the value when the
          count drops back to 1 so we never submit a stale entry the user
          can no longer see. */}
      {total > 1 ? (
        <Textarea
          label="Per-vehicle notes"
          value={value.notes}
          onChange={(e) => patch({ notes: e.target.value })}
          rows={2}
          autoResize
          id={`${idBase}-notes`}
        />
      ) : null}

      {duplicateWarning ? (
        <p className={styles.dupWarning} role="status">
          ⚠ {duplicateWarning}
        </p>
      ) : null}
    </fieldset>
  );
}
